import { client } from './client';
import type { ApiResponse } from '@/types/api';
import type {
  DashboardOverview,
  DashboardStudentsListResponse,
  DashboardStudentsListParams,
  DashboardStudentSearchItem,
  PracticeSeedSearchItem,
  StudentDetailResponse,
} from '@/types/dashboard';
import type {
  DashboardAssignmentsListResponse,
  DashboardAssignmentsListParams,
} from '@/types/assignment';

export async function getOverview(): Promise<DashboardOverview> {
  const { data } = await client.get<ApiResponse<DashboardOverview>>('/api/v1/dashboard/overview');
  return data.data;
}

export async function getStudents(
  params?: DashboardStudentsListParams,
): Promise<DashboardStudentsListResponse> {
  // Axios serializes arrays as `tag_id[]=a&tag_id[]=b` by default, which
  // Go's r.URL.Query() reads under the `tag_id[]` key instead of `tag_id`.
  // Flatten to a comma-joined scalar so the backend's
  // strings.Split(..., ",") picks up every id.
  const queryParams: Record<string, string | number | undefined> = {
    page: params?.page,
    per_page: params?.per_page,
    sort_by: params?.sort_by,
    sort_order: params?.sort_order,
    status: params?.status,
  };
  if (params?.search && params.search.trim() !== '') {
    queryParams.search = params.search.trim();
  }
  if (params?.tag_id && params.tag_id.length > 0) {
    queryParams.tag_id = params.tag_id.join(',');
  }
  const { data } = await client.get<ApiResponse<DashboardStudentsListResponse>>(
    '/api/v1/dashboard/students',
    { params: queryParams },
  );
  return data.data;
}

export async function getStudentDetail(id: string): Promise<StudentDetailResponse> {
  const { data } = await client.get<ApiResponse<StudentDetailResponse>>(
    `/api/v1/dashboard/students/${id}`,
  );
  return data.data;
}

export async function getAssignments(
  params?: DashboardAssignmentsListParams,
): Promise<DashboardAssignmentsListResponse> {
  const { data } = await client.get<ApiResponse<DashboardAssignmentsListResponse>>(
    '/api/v1/dashboard/assignments',
    { params },
  );
  return data.data;
}

export async function searchStudents(
  search: string,
  limit = 20,
): Promise<DashboardStudentSearchItem[]> {
  const { data } = await client.get<ApiResponse<DashboardStudentSearchItem[]>>(
    '/api/v1/dashboard/students/search',
    { params: { search, limit } },
  );
  return data.data;
}

export async function searchPracticeSeeds(
  search: string,
  limit = 20,
): Promise<PracticeSeedSearchItem[]> {
  const { data } = await client.get<ApiResponse<PracticeSeedSearchItem[]>>(
    '/api/v1/dashboard/practice-seeds/search',
    { params: { search, limit } },
  );
  return data.data;
}
